import { useState, useCallback } from 'react';
import { MOCK_USERS } from '../../assets/mockData';
import { useUser } from '../../contexts/UserContext';
import { usePosts } from '../../contexts/PostContext';
import LikeButton from './LikeButton';
import CommentSection from './CommentSection';

function getUser(userId, currentUser) {
  if (userId === 'currentUser' || userId === currentUser.id) return currentUser;
  return MOCK_USERS.find((u) => u.id === userId);
}

function timeAgo(ts) {
  const diff = Date.now() - ts;
  if (diff < 60000) return '刚刚';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`;
  return `${Math.floor(diff / 86400000)}天前`;
}

function AvatarPlaceholder({ name }) {
  const colors = ['#6c5ce7', '#00b894', '#e17055', '#0984e3', '#fdcb6e', '#e84393'];
  const color = colors[name.charCodeAt(0) % colors.length];
  return (
    <div className="avatar-placeholder" style={{ background: color }}>
      {name[0]}
    </div>
  );
}

export default function PostCard({ post }) {
  const [showComments, setShowComments] = useState(false);
  const [imgViewer, setImgViewer] = useState(null);
  const { user } = useUser();
  const { toggleLike, addComment, deletePost } = usePosts();
  const author = getUser(post.userId, user);
  const liked = post.likes.includes('currentUser') || post.likes.includes(user.id);

  const handleLike = useCallback(() => {
    toggleLike(post.id, 'currentUser');
  }, [post.id, toggleLike]);

  const handleComment = useCallback((content) => {
    addComment(post.id, 'currentUser', content);
  }, [post.id, addComment]);

  const isOwn = post.userId === 'currentUser' || post.userId === user.id;

  return (
    <div className="post-card">
      <div className="post-header">
        <div className="post-author-info">
          {author?.avatar ? (
            <img src={author.avatar} alt="" className="post-avatar" />
          ) : (
            <AvatarPlaceholder name={author?.name || '?'} />
          )}
          <div>
            <div className="post-author-name">{author?.name}</div>
            <div className="post-time">{timeAgo(post.createdAt)}</div>
          </div>
        </div>
        {isOwn && (
          <button className="post-delete-btn" onClick={() => deletePost(post.id)}>
            删除
          </button>
        )}
      </div>

      <div className="post-content">{post.content}</div>

      {post.images?.length > 0 && (
        <div className={`post-images grid-${Math.min(post.images.length, 3)}`}>
          {post.images.map((src, i) => (
            <img
              key={i}
              src={src}
              alt=""
              className="post-image"
              onClick={() => setImgViewer(src)}
              loading="lazy"
            />
          ))}
        </div>
      )}

      {post.video && (
        <div className="post-video">
          <video src={post.video} controls preload="metadata" />
        </div>
      )}

      {post.tags?.length > 0 && (
        <div className="post-tags">
          {post.tags.map((t) => (
            <span key={t} className="post-tag">#{t}</span>
          ))}
        </div>
      )}

      <div className="post-actions">
        <LikeButton liked={liked} count={post.likes.length} onToggle={handleLike} />
        <button
          className={`comment-toggle-btn ${showComments ? 'active' : ''}`}
          onClick={() => setShowComments(!showComments)}
        >
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path
              d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>{post.comments.length || ''}</span>
        </button>
      </div>

      <CommentSection
        comments={post.comments}
        visible={showComments}
        onAddComment={handleComment}
      />

      {imgViewer && (
        <div className="image-viewer" onClick={() => setImgViewer(null)}>
          <img src={imgViewer} alt="" />
        </div>
      )}
    </div>
  );
}
